import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import _ from 'lodash';

import translationsEnJson from '../../../public/locale/transperfect/en/translation.json';
import {
  getAoeCodesForEAP,
  getEapBHProgramId,
} from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { Option, ProviderSearchFilters } from '../../models/ProviderSearch';
import { compareSharePopoverStyles } from '../CompareShareToggle/CompareShareToggle.styled';
import {
  Constants,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  ReverseCoverageTypesCodes,
  SectionType,
  VirtualCareOptions,
} from '../Constants';
import { getBestMatchOptionKey } from '../ContentFilters/SortByUtil';

export const getDistanceFilterOption = (
  value: number,
  previousValue?: number
) => [
  {
    label: `${value} ${translationsEnJson.miles}`,
    value,
    dnt_label: `${value} ${translationsEnJson.miles}`,
    c_value: value,
    hide: !value,
    previousValue,
  },
];

export const getFilterValue = (
  filterData: ProviderSearchFilters,
  model: FilterCollectionModel
) =>
  Array.isArray(filterData?.[model])
    ? filterData?.[model].length > 0
      ? filterData?.[model]?.[0]?.value
      : false
    : filterData?.[model]?.value;

export const getAllFilterValue = (
  filterData: ProviderSearchFilters,
  model: FilterCollectionModel
) =>
  Array.isArray(filterData?.[model])
    ? filterData?.[model]
      ? filterData?.[model].map((option) => option.value).toString()
      : false
    : filterData?.[model]?.value;

export const getLatestFilterValue = (
  filterData: ProviderSearchFilters,
  model: FilterCollectionModel
) =>
  Array.isArray(filterData?.[model])
    ? filterData?.[model].length > 0
      ? filterData?.[model]
      : []
    : filterData?.[model]?.value;

export const getIsVirtualCareV2 = (
  enableBHVirtualCareFilterV2: boolean,
  coverageType: string | undefined,
  sectionType: string | undefined
) =>
  enableBHVirtualCareFilterV2 &&
  coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
  sectionType === SectionType.PROVIDER;

export const getVirtualCareModel = (
  enableBHVirtualCareFilterV2: boolean,
  coverageType: string | undefined,
  sectionType: string | undefined
) => {
  const isVirtualCareV2 = getIsVirtualCareV2(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );
  return isVirtualCareV2
    ? FilterCollectionModel.VIRTUAL_CARE_OPTIONS
    : FilterCollectionModel.VIRTUAL_CARE;
};

const setEAPOptionForEAPClientOnly = (
  eapOnlyClientOption: Option[],
  filterData: ProviderSearchFilters
) => {
  if (
    !(
      filterData[FilterCollectionModel.EAP_OFFERED] &&
      filterData[FilterCollectionModel.EAP_OFFERED].length > 0
    )
  ) {
    filterData = {
      ...filterData,
      [FilterCollectionModel.EAP_OFFERED]: eapOnlyClientOption,
    };
  }
  return filterData;
};

// Helper function to handle EAP offered to AOE transfer when EAP is added
const handleEapOfferedAdded = (
  filterData: ProviderSearchFilters,
  aoeOptions: any
) => {
  if (
    filterData[FilterCollectionModel.AREA_OF_EXPERTISE] &&
    filterData[FilterCollectionModel.AREA_OF_EXPERTISE]?.length > 0 &&
    filterData[FilterCollectionModel.EAP_OFFERED] !== undefined
  ) {
    return {
      ...filterData,
      [FilterCollectionModel.AREA_OF_EXPERTISE]: aoeOptions?.concat(
        filterData[FilterCollectionModel.EAP_OFFERED]
      ),
    };
  }
  return {
    ...filterData,
    [FilterCollectionModel.AREA_OF_EXPERTISE]:
      filterData[FilterCollectionModel.EAP_OFFERED],
  };
};

// Helper function to handle EAP offered removal
const handleEapOfferedRemoved = (filterData: ProviderSearchFilters) => ({
  ...filterData,
  [FilterCollectionModel.AREA_OF_EXPERTISE]: filterData[
    FilterCollectionModel.AREA_OF_EXPERTISE
  ]?.filter((option) => option.dnt_label !== EAP_OPTION_LABEL),
});

// Helper function to handle AOE updates
const handleAoeUpdated = (
  filterData: ProviderSearchFilters,
  eapOptionsfromAoe: any,
  aoeOptions: any
) => {
  const updatedData = { ...filterData };

  if (eapOptionsfromAoe && eapOptionsfromAoe.length > 0) {
    updatedData[FilterCollectionModel.EAP_OFFERED] = eapOptionsfromAoe;
  } else {
    updatedData[FilterCollectionModel.EAP_OFFERED] = [];
  }

  if (aoeOptions && aoeOptions.length > 0) {
    updatedData[FilterCollectionModel.AREA_OF_EXPERTISE] = aoeOptions;
  }

  return updatedData;
};

export const resolveSelectedFilters = (
  enhanceDistanceFilter: boolean,
  coverageType: string,
  filterData: ProviderSearchFilters,
  selectedFilters: ProviderSearchFilters,
  enableBHVirtualCareFilterV2: boolean,
  sectionType: string,
  defaultSearchRadius = 5
): ProviderSearchFilters => {
  // We need to clean this PlatinumProviderIndicator filter if it is selected on Map View Page
  if (
    Array.isArray(filterData?.PlatinumProviderIndicator) &&
    filterData?.PlatinumProviderIndicator.length > 0
  ) {
    filterData.PlatinumProviderIndicator =
      filterData.PlatinumProviderIndicator[0];
  }

  // We need to clean this ProviderScheduling filter if it is selected on Map View Page
  if (
    Array.isArray(filterData?.ProviderScheduling) &&
    filterData?.ProviderScheduling.length > 0
  ) {
    filterData.ProviderScheduling = filterData.ProviderScheduling[0];
  }

  // Update distance filter value based on virtual care filter value
  // only for Behavioral Health coverage type
  if (
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    enhanceDistanceFilter
  ) {
    const virtualCareModel = getVirtualCareModel(
      enableBHVirtualCareFilterV2,
      coverageType,
      sectionType
    );

    const currentVirtualCareFilterValue = getFilterValue(
      filterData,
      virtualCareModel
    );
    const previousVirtualCareFilterValue = getFilterValue(
      selectedFilters,
      virtualCareModel
    );
    const distanceFilterValue = getFilterValue(
      selectedFilters,
      FilterCollectionModel.DISTANCE
    ) as number;

    // remove distance filter value when virtual care filter value is changed to true
    if (
      currentVirtualCareFilterValue === true &&
      previousVirtualCareFilterValue !== true &&
      distanceFilterValue
    ) {
      filterData = {
        ...filterData,
        [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
          0,
          distanceFilterValue
        ),
      };
      // set distance filter value from previous, when virtual care filter value is changed to false
    } else if (
      currentVirtualCareFilterValue === false &&
      previousVirtualCareFilterValue !== false &&
      !distanceFilterValue
    ) {
      filterData = {
        ...filterData,
        [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
          (filterData?.[FilterCollectionModel.DISTANCE]?.[0]
            ?.previousValue as number) ||
            filterData?.[FilterCollectionModel.AUTO_SEARCH_RADIUS] ||
            defaultSearchRadius
        ),
      };
    }

    // Transfering EAP offered value to Area of Expertise (Vice Versa)
    // EAR offered
    const currentEapOfferedFilterValue = getFilterValue(
      filterData,
      FilterCollectionModel.EAP_OFFERED
    );
    const previousEapOfferedFilterValue = getFilterValue(
      selectedFilters,
      FilterCollectionModel.EAP_OFFERED
    );
    const currentAoeFilterValue = getAllFilterValue(
      filterData,
      FilterCollectionModel.AREA_OF_EXPERTISE
    );
    const previousAoeFilterValue = getAllFilterValue(
      selectedFilters,
      FilterCollectionModel.AREA_OF_EXPERTISE
    );
    const aoeOptions = filterData[FilterCollectionModel.AREA_OF_EXPERTISE];
    const eapOptionsfromAoe = filterData[
      FilterCollectionModel.AREA_OF_EXPERTISE
    ]?.filter((option) => option.dnt_label === EAP_OPTION_LABEL);

    // pass EAP offer selected to AOE (vice-versa)
    if (currentEapOfferedFilterValue && !previousEapOfferedFilterValue) {
      filterData = handleEapOfferedAdded(filterData, aoeOptions);
    } else if (
      !currentEapOfferedFilterValue &&
      previousEapOfferedFilterValue &&
      currentAoeFilterValue === previousAoeFilterValue
    ) {
      // If EAP offered is cleared and Area of Expertise is same as previous, then remove EAP offered from Area of Expertise
      filterData = handleEapOfferedRemoved(filterData);
    } else if (currentAoeFilterValue !== previousAoeFilterValue) {
      // IF AOE is updated
      filterData = handleAoeUpdated(filterData, eapOptionsfromAoe, aoeOptions);
    }

    const eapOnlyClientOption: Option[] = JSON.parse(
      localStorage.getItem('eapOnlyClientOption') ?? '[]'
    );
    if (eapOnlyClientOption.length > 0) {
      filterData = setEAPOptionForEAPClientOnly(
        eapOnlyClientOption,
        filterData
      );
    }

    return filterData;
  } else {
    return filterData;
  }
};

export const getPreviousDistanceOrDefaultOption = (
  enhanceDistanceFilter: boolean,
  coverageType: string,
  model: FilterCollectionModel,
  selectedFilters: ProviderSearchFilters,
  enableBHVirtualCareFilterV2: boolean,
  sectionType: string,
  defaultSearchRadius: number
) => {
  // If the coverage type is Behavioral Health and a previous distance value is available,
  // set the distance filter to the previous value when the virtual care filter is cleared
  const distanceFilterValue = getFilterValue(
    selectedFilters,
    FilterCollectionModel.DISTANCE
  ) as number;

  const virtualCareModel = getVirtualCareModel(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );

  if (
    (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
      enhanceDistanceFilter) &&
    model === virtualCareModel &&
    !distanceFilterValue
  ) {
    return {
      [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
        (selectedFilters?.[FilterCollectionModel.DISTANCE]?.[0]
          ?.previousValue as number) ||
          selectedFilters?.[FilterCollectionModel.AUTO_SEARCH_RADIUS] ||
          defaultSearchRadius
      ),
    };
  }
  return {};
};

const resolvedFilters = (selectedFilters) => {
  const resolvedFilters = { ...selectedFilters };
  if (resolvedFilters?.AutoSearchRadius) {
    delete resolvedFilters.AutoSearchRadius;
  }
  Object.keys(resolvedFilters).forEach((key) => {
    if (
      resolvedFilters[key] === null ||
      resolvedFilters[key] === '' ||
      (Array.isArray(resolvedFilters[key]) &&
        resolvedFilters[key].length === 0) ||
      Object.keys(resolvedFilters[key]).length === 0
    ) {
      delete resolvedFilters[key];
    }
  });
  return resolvedFilters;
};

export const areFiltersEqual = (filtersA, filtersB) => {
  const resolvedFilterA = resolvedFilters(filtersA);
  const resolvedFilterB = resolvedFilters(filtersB);
  return JSON.stringify(resolvedFilterA) === JSON.stringify(resolvedFilterB);
};

export const getFiltersWithoutDistance = (
  searchFilters: ProviderSearchFilters
) => _.omit(searchFilters, ['Distance', 'AutoSearchRadius']);

export const processBHDefaultFacilityFilters = (section, filters) => {
  const { options } =
    filters.find(
      (filter) => filter.model === FilterCollectionModel.PROGRAM_CATEGORY
    ) || {};
  const programIDs = section?.programIDs?.replaceAll(' ', '') || '';

  const sectionName = options?.find(
    (option) => option?.value?.toString().replaceAll(' ', '') === programIDs
  );

  if (!sectionName) {
    return [];
  }

  return [
    {
      label: sectionName.label,
      value: programIDs,
      hide: false,
      dnt_label: sectionName.dnt_label,
    },
  ];
};

export const getBrowseServicesPreSelectedFilters = ({
  isBHFacility,
  virtualCare,
  section,
  formattedAOEIds,
  preSelectEapFilter,
  coverageType,
  searchRadius,
  filters,
  t,
}) => {
  const eapCodes = getAoeCodesForEAP();
  const selectedFilters: ProviderSearchFilters = {};
  if (isBHFacility) {
    const programCategory = processBHDefaultFacilityFilters(section, filters);
    if (programCategory.length) {
      selectedFilters.ProgramCategory = programCategory;
    }
  }

  if (virtualCare) {
    selectedFilters.VirtualCare = {
      label: t('FILTER_OPTIONS.VirtualCare.Y'),
      value: true,
      hide: false,
      dnt_label: translationsEnJson.FILTER_OPTIONS.VirtualCare.Y,
    };
  }
  if (formattedAOEIds.length > 0) {
    selectedFilters.AreaOfExpertise = formattedAOEIds?.map((aoeId) => ({
      label: `${aoeId}-${t('CONTENT_FILTERS.AREAS_OF_EXPERTISE')}`,
      value: aoeId,
      hide: !aoeId,
      dnt_label: translationsEnJson.CONTENT_FILTERS.AREAS_OF_EXPERTISE,
    }));
  }
  if (preSelectEapFilter) {
    selectedFilters.EapOffered = [
      {
        label: t(EAP_OPTION_LABEL),
        value: eapCodes.join(','),
        dnt_label: EAP_OPTION_LABEL,
      },
    ];
    const eapBHProgramId = getEapBHProgramId();
    if (eapBHProgramId) {
      selectedFilters.BehavioralProgramIndicator = [
        {
          label: t(EAP_OPTION_LABEL),
          value: eapBHProgramId,
          dnt_label: EAP_OPTION_LABEL,
        },
      ];
    }
  }
  const BEST_MATCH_SORT_LABEL_KEY = getBestMatchOptionKey(coverageType);
  selectedFilters[FilterCollectionModel.SORT_BY] = {
    label: t(`SORT_OPTIONS.${BEST_MATCH_SORT_LABEL_KEY}`),
    value: Constants.PROVIDER_SEARCH_SORT_BY,
    hide: true,
    dnt_label: translationsEnJson.SORT_OPTIONS[BEST_MATCH_SORT_LABEL_KEY],
  };

  selectedFilters[FilterCollectionModel.AUTO_SEARCH_RADIUS] = searchRadius;
  selectedFilters[FilterCollectionModel.DISTANCE] =
    getDistanceFilterOption(searchRadius);

  return selectedFilters;
};

export const processDefaultFacilityFilters = (section, filters) => {
  const programIDs = section?.bhFacilityProgramIds?.replaceAll(' ', '') || '';

  if (!programIDs) {
    return [];
  }

  const { options } =
    filters?.find(
      (filter) => filter?.model === FilterCollectionModel.PROGRAM_CATEGORY
    ) || {};

  const optionValuePrograms = options?.find((option) => {
    const valueArray = option?.value?.replaceAll(' ', '')?.split(',');

    return valueArray.some((programID) =>
      programIDs
        .split(',')
        .map((programID) => programID.trim())
        .includes(programID.trim())
    );
  });

  if (!optionValuePrograms) {
    return [];
  }

  return [
    {
      label: optionValuePrograms?.label,
      value: optionValuePrograms?.value,
      hide: false,
      dnt_label: optionValuePrograms?.dnt_label,
    },
  ];
};

export const getBrowseLabsAndServicesPreSelectFilters = ({
  section,
  filters,
}) => {
  const selectedFilters: ProviderSearchFilters = {};
  const programCategory = processDefaultFacilityFilters(section, filters);
  if (programCategory.length) {
    selectedFilters.ProgramCategory = programCategory;
  }
  return selectedFilters;
};

export const getIssVirtualCareSelected = (
  searchFilters: ProviderSearchFilters,
  virtualCareModel: string
) => {
  const virtualCareFilterValue = searchFilters[virtualCareModel];
  return Array.isArray(virtualCareFilterValue)
    ? virtualCareFilterValue.length > 0
    : virtualCareFilterValue?.value;
};

export const disableDistanceFilter = (
  model: FilterCollectionModel,
  isVirtualCareFilterSelected: boolean,
  selectedFilters: ProviderSearchFilters,
  enhanceDistanceFilter: boolean
) =>
  enhanceDistanceFilter &&
  model === FilterCollectionModel.DISTANCE &&
  (selectedFilters[FilterCollectionModel.VIRTUAL_CARE_OPTIONS]?.[0]?.value ===
    VirtualCareOptions.OFFERS_VIRTUAL_VISITS ||
    isVirtualCareFilterSelected);

export const renderFilterLabel = (
  showUpcomingAvailabilityFilter: boolean,
  showCoachMarkUpcomingAvailability: boolean,
  handleOpenChange: (open: boolean) => void,
  filterSortScreen: boolean,
  portalConfigCheck: boolean,
  upcomingAvailabilityMessageTitle: string,
  label: string,
  isDistance: boolean,
  distanceTitle: string,
  showSelectedOptions: boolean,
  selectedOptions: number,
  showDistanceCoachMark: boolean,
  showCoachMarkWebDistance: boolean,
  defaultSearchRadiusMessageTitle: string,
  popOverContent: React.ReactNode,
  popOverContentUpcomingAvailability: React.ReactNode,
  showPrioritySchedulingFilter?: boolean,
  showCoachMarkPriorityScheduling?: boolean,
  prioritySchedulingMessageTitle?: string,
  popOverContentPriorityScheduling?: React.ReactNode
) => {
  if (showUpcomingAvailabilityFilter) {
    return (
      <Popover
        align="center"
        content={popOverContentUpcomingAvailability}
        css={{
          ...compareSharePopoverStyles,
          '.abyss-button-root': {
            color: '#4B4D4F',
            backgroundColor: '#FFFFFF',
            border: '1px solid #4B4D4F',
          },
          'abyss-popover-close-button': {
            boxShadow: 'none !important',
          },
          '.abyss-icon-symbol': {
            // @ts-expect-error ignore style check
            '&:focus-visible': {
              boxShadow: 'none',
            },
          },
          'abyss-popover-content-container': {
            overflow: '$popoverContentOverflow',
            maxHeight: '$popoverContentHeight',
          },
          '& .abyss-popover-content-container p': {
            marginBottom: '10px',
          },
        }}
        data-testid="popover"
        onOpenChange={handleOpenChange}
        open={showCoachMarkUpcomingAvailability && !filterSortScreen}
        position="bottom"
        positionOffset={17}
        style={{ zIndex: portalConfigCheck ? '49' : '999' }}
        title={upcomingAvailabilityMessageTitle}
      >
        <div
          style={{
            lineHeight: '20px',
            color: '$core.color.neutral.90',
            fontWeight: '600',
            fontSize: '16px',
          }}
        >
          {label}
        </div>
      </Popover>
    );
  }
  if (showPrioritySchedulingFilter) {
    return (
      <Popover
        align="center"
        content={popOverContentPriorityScheduling}
        css={{
          ...compareSharePopoverStyles,
          '.abyss-button-root': {
            color: '#4B4D4F',
            backgroundColor: '#FFFFFF',
            border: '1px solid #4B4D4F',
          },
          'abyss-popover-close-button': {
            boxShadow: 'none !important',
          },
          '.abyss-icon-symbol': {
            // @ts-expect-error ignore style check
            '&:focus-visible': {
              boxShadow: 'none',
            },
          },
          'abyss-popover-content-container': {
            overflow: '$popoverContentOverflow',
            maxHeight: '$popoverContentHeight',
          },
          '& .abyss-popover-content-container p': {
            marginBottom: '10px',
          },
        }}
        data-testid="popover"
        onOpenChange={handleOpenChange}
        open={showCoachMarkPriorityScheduling && !filterSortScreen}
        position="bottom"
        positionOffset={17}
        style={{ zIndex: portalConfigCheck ? '49' : '999' }}
        title={prioritySchedulingMessageTitle}
      >
        <div
          style={{
            lineHeight: '20px',
            color: '$core.color.neutral.90',
            fontWeight: '600',
            fontSize: '16px',
          }}
        >
          {label}
        </div>
      </Popover>
    );
  }
  if (isDistance) {
    const distanceDiv = (
      <div
        style={{
          lineHeight: '20px',
          color: '$core.color.neutral.90',
          fontWeight: '600',
          fontSize: '16px',
        }}
      >
        {distanceTitle}
      </div>
    );
    if (showDistanceCoachMark) {
      return (
        <Popover
          align="center"
          content={popOverContent}
          css={{
            ...compareSharePopoverStyles,
            '.abyss-button-root': {
              color: '#4B4D4F',
              backgroundColor: '#FFFFFF',
              border: '1px solid #4B4D4F',
            },
            'abyss-popover-close-button': {
              boxShadow: 'none !important',
            },
            '.abyss-icon-symbol': {
              // @ts-expect-error ignore style check
              '&:focus-visible': {
                boxShadow: 'none',
              },
            },
          }}
          data-testid="popover"
          onOpenChange={handleOpenChange}
          open={showCoachMarkWebDistance && !filterSortScreen}
          position="bottom"
          positionOffset={17}
          style={{ zIndex: 999 }}
          title={defaultSearchRadiusMessageTitle}
        >
          {distanceDiv}
        </Popover>
      );
    }

    return distanceDiv;
  }

  return (
    <Text fontWeight="$semibold" size="$md">
      {label} {showSelectedOptions ? `(${selectedOptions})` : ''}
    </Text>
  );
};
